/**
 * ClientAvatar Component (Story 3.4)
 *
 * Displays a client logo image or a fallback avatar with initials.
 * Used in client list cards, edit sheet, and detail pages.
 *
 * Features:
 * - Lazy loading for off-screen images (loading="lazy")
 * - onError fallback to initials when image fails to load
 * - Deterministic color from client name hash
 * - Size variants: sm (28px), md (40px), lg (64px)
 */

import { useState, useEffect } from "react";
import { cn } from "@provenleap/ui";

const AVATAR_COLORS = [
  "#FF6D6F",
  "#4F46E5",
  "#059669",
  "#D97706",
  "#7C3AED",
  "#DB2777",
  "#0891B2",
  "#65A30D",
  "#DC2626",
  "#2563EB",
];

const SIZE_MAP = {
  sm: "h-7 w-7 text-[11px]",
  md: "h-10 w-10 text-sm",
  lg: "h-16 w-16 text-lg",
} as const;

const IMG_SIZE_MAP = {
  sm: 28,
  md: 40,
  lg: 64,
} as const;

export type ClientAvatarSize = keyof typeof SIZE_MAP;

interface ClientAvatarProps {
  /**
   * Client name. Nullable so call sites that may carry a null name (e.g. a
   * roll-up dashboard row whose `clientName` is null after the Story 40-7 LEFT
   * join) can pass it directly — it is coerced to "" internally so no derived
   * string op (`.trim()` / `.length` / `.charCodeAt`) can ever throw.
   */
  name: string | null | undefined;
  logoUrl: string | null;
  size?: ClientAvatarSize;
  className?: string;
}

/**
 * Get deterministic color from client name hash.
 * Null/undefined names coerce to "" (the hash falls through to the first color).
 */
export function getAvatarColor(name: string | null | undefined): string {
  const safe = name ?? "";
  let hash = 0;
  for (let i = 0; i < safe.length; i++) {
    hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

/**
 * Get initials from client name.
 * First letter of first two words; if single word, first two characters.
 * Null/undefined names coerce to "" (returns an empty string).
 */
export function getClientInitials(name: string | null | undefined): string {
  const safe = name ?? "";
  const words = safe.trim().split(/\s+/);
  if (words.length >= 2 && words[0] && words[1]) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return safe.substring(0, 2).toUpperCase();
}

export function ClientAvatar({
  name,
  logoUrl,
  size = "md",
  className,
}: ClientAvatarProps) {
  const [imgError, setImgError] = useState(false);

  // Reset error state when logoUrl changes (e.g., after new upload)
  useEffect(() => setImgError(false), [logoUrl]);

  // Coerce a null/undefined name to "" so the labels never read "null avatar".
  const safeName = name ?? "";
  const initials = getClientInitials(safeName);
  const color = getAvatarColor(safeName);
  const showImage = logoUrl && !imgError;
  const pixelSize = IMG_SIZE_MAP[size];

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full font-medium text-white overflow-hidden shrink-0",
        SIZE_MAP[size],
        className
      )}
      style={showImage ? undefined : { backgroundColor: color }}
      aria-label={`${safeName} avatar`}
    >
      {showImage ? (
        <img
          src={logoUrl}
          alt={`${safeName} logo`}
          loading="lazy"
          width={pixelSize}
          height={pixelSize}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        initials
      )}
    </div>
  );
}
